import { Button } from "primereact/button";

import {
  actionTooltipProps,
  ErrorState,
  ExportTableDialog,
  HubTable,
  Icon,
  TableSettingsDialog,
} from "@/ds";
import type { PermissionSortField } from "@/modules/permissions";
import { hubTableCartao, HubTableCartao } from "@/shared/components/hub-table-cartao";
import { ListaToolbar } from "@/shared/components/lista-toolbar";
import { EditPermissionDialog } from "./components/edit-permission-dialog";
import {
  PERMISSIONS_ACTIONS_HEADER,
  PERMISSIONS_ACTIONS_KEY,
  PERMISSIONS_ACTIONS_WIDTH,
  PERMISSIONS_COLUMNS,
  PERMISSIONS_ERROR_MESSAGE,
  PERMISSIONS_EXPORT_LIST_NAME,
  PERMISSIONS_LOADING_LABEL,
  PERMISSIONS_ROWS_PER_PAGE_LABEL,
  PERMISSIONS_SEARCH_PLACEHOLDER,
} from "./permissions.constants";
import type { PermissionsColumnKey } from "./permissions.constants";
import styles from "./permissions.module.scss";
import type { PermissionLinha, PermissionsViewModel } from "./types-permissions";

const EDIT_TOOLTIP = actionTooltipProps("Editar", { event: "hover" });

/** Aba Permissões (Figma 4434:39407): busca e ⋮ sobre a tabela; o lápis ou a linha abre a edição, sem botão de criar. */
export function PermissionsView({
  isMobile,
  search,
  buscar,
  settings,
  exportView,
  notifySettingsApplied,
  items,
  total,
  isFirstLoad,
  status,
  errorMessage,
  reload,
  table,
  dataTableProps,
  hasSearch,
  limparBusca,
  podeEditar,
  editando,
  editar,
  fecharEdicao,
  salvarEdicao,
}: PermissionsViewModel) {
  return (
    <section className={styles.container} data-testid="permissions-screen">
      <ListaToolbar
        testIdPrefix="permissions"
        isMobile={isMobile}
        search={search}
        onSearchChange={buscar}
        searchPlaceholder={PERMISSIONS_SEARCH_PLACEHOLDER}
        searchWidth="13.625rem"
        inlineActions
        onExport={exportView.open}
        onOpenSettings={settings.dialog.open}
      />

      {exportView.isOpen ? (
        <ExportTableDialog
          listName={PERMISSIONS_EXPORT_LIST_NAME}
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      <TableSettingsDialog
        dialog={settings.dialog}
        testId="permissions-table-settings"
        onApplied={notifySettingsApplied}
      />

      {editando ? (
        // A chave descarta o formulário ao trocar de permissão.
        <EditPermissionDialog
          key={editando.id}
          permission={editando}
          onSubmit={(values) => salvarEdicao(editando, values)}
          onCancel={fecharEdicao}
        />
      ) : null}

      <div className={styles.content}>
        <HubTableCartao<PermissionLinha, PermissionsColumnKey, PermissionSortField>
          value={items}
          totalRecords={total}
          table={table}
          settings={settings}
          status={status}
          errorMessage={errorMessage}
          loadingLabel={PERMISSIONS_LOADING_LABEL}
          onRowClick={editar}
          emptyNoun="permissão"
          emptyGender="f"
          emptyPlural="permissões"
          hasSearch={hasSearch}
          onClear={hasSearch ? limparBusca : undefined}
          dataTableProps={dataTableProps}
          rowsPerPageLabel={PERMISSIONS_ROWS_PER_PAGE_LABEL}
          testId="permissions"
          data-testid="permissions-table-wrapper"
          data-first-load={isFirstLoad || undefined}
        >
          {PERMISSIONS_COLUMNS.map((coluna) => (
            <HubTable.Column<PermissionLinha, PermissionsColumnKey>
              key={coluna.key}
              field={coluna.key}
              columnKey={coluna.key}
              header={coluna.header}
              style={coluna.width ? { width: coluna.width, minWidth: coluna.width } : undefined}
              body={(linha) => linha.permission[coluna.key]}
            />
          ))}
          <HubTable.Column<PermissionLinha, PermissionsColumnKey>
            field={PERMISSIONS_ACTIONS_KEY}
            columnKey={PERMISSIONS_ACTIONS_KEY}
            header={PERMISSIONS_ACTIONS_HEADER}
            sortable={false}
            hidden={!podeEditar}
            align="center"
            alignHeader="center"
            bodyClassName={hubTableCartao.actionCellClassName}
            headerClassName={hubTableCartao.actionCellClassName}
            style={{ width: PERMISSIONS_ACTIONS_WIDTH, minWidth: PERMISSIONS_ACTIONS_WIDTH }}
            body={(linha) => (
              <Button
                type="button"
                className={hubTableCartao.actionButtonClassName}
                icon={<Icon name="pencil" size={14} />}
                aria-label={linha.editLabel}
                aria-haspopup="dialog"
                tooltip={EDIT_TOOLTIP.content as string}
                tooltipOptions={{ position: EDIT_TOOLTIP.position, event: "hover" }}
                onClick={() => editar?.(linha)}
                data-testid={linha.editTestId}
                severity="secondary"
                text
              />
            )}
          />
          <HubTable.Error>
            <ErrorState
              message={PERMISSIONS_ERROR_MESSAGE}
              description={errorMessage ?? undefined}
              onRetry={reload}
              testId="permissions-error"
            />
          </HubTable.Error>
        </HubTableCartao>
      </div>
    </section>
  );
}
